import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type {
  CreateContractInput,
  Currency,
  RateType,
} from '@/lib/contracts/types';

interface ContractFormProps {
  initial?: Partial<CreateContractInput>;
  submitLabel: string;
  pending?: boolean;
  onSubmit: (value: CreateContractInput) => void | Promise<void>;
}

const CURRENCIES: Currency[] = ['USD', 'BRL', 'CLP', 'EUR'];

export function ContractForm({ initial, submitLabel, pending = false, onSubmit }: ContractFormProps) {
  const { t } = useTranslation('network');
  const [hotel, setHotel] = useState(initial?.hotelProviderName ?? '');
  const [airport, setAirport] = useState(initial?.airportCode ?? '');
  const [rateType, setRateType] = useState<RateType>(initial?.rateType ?? 'fixed');
  const [ratePerNight, setRatePerNight] = useState<number>(initial?.ratePerNight ?? 0);
  const [currency, setCurrency] = useState<Currency>(initial?.currency ?? 'USD');
  const [guaranteedRooms, setGuaranteedRooms] = useState<number>(initial?.guaranteedRooms ?? 0);
  const [minStars, setMinStars] = useState<number>(initial?.minStars ?? 3);
  const [validFrom, setValidFrom] = useState(initial?.validFrom?.slice(0, 10) ?? '');
  const [validUntil, setValidUntil] = useState(initial?.validUntil?.slice(0, 10) ?? '');
  const [terms, setTerms] = useState(initial?.terms ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onSubmit({
      hotelProviderName: hotel.trim(),
      airportCode: airport.trim().toUpperCase(),
      rateType,
      ratePerNight: Number(ratePerNight),
      currency,
      guaranteedRooms: Number(guaranteedRooms),
      minStars: Number(minStars),
      validFrom: validFrom ? new Date(validFrom).toISOString() : '',
      validUntil: validUntil ? new Date(validUntil).toISOString() : '',
      terms: terms.trim() || undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      aria-label={t('contracts.form.label')}
    >
      <Field label={t('contracts.form.hotelName')}>
        <input
          required
          value={hotel}
          onChange={(e) => setHotel(e.target.value)}
          className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label={t('contracts.form.airport')}>
          <input
            required
            maxLength={3}
            value={airport}
            onChange={(e) => setAirport(e.target.value.toUpperCase())}
            placeholder="SCL"
            className="w-24 rounded-md border border-input bg-background px-2 py-1.5 text-sm uppercase"
          />
        </Field>
        <Field label={t('contracts.form.minStars')}>
          <input
            type="number"
            min={1}
            max={5}
            value={minStars}
            onChange={(e) => setMinStars(Number(e.target.value))}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        </Field>
      </div>

      <fieldset className="rounded-md border border-input p-4">
        <legend className="px-2 text-sm font-medium">{t('contracts.form.rate')}</legend>
        <div className="mt-2 grid grid-cols-3 gap-4">
          <Field label={t('contracts.form.rateType')}>
            <select
              value={rateType}
              onChange={(e) => setRateType(e.target.value as RateType)}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="fixed">{t('contracts.form.rateFixed')}</option>
              <option value="dynamic">{t('contracts.form.rateDynamic')}</option>
            </select>
          </Field>
          <Field label={t('contracts.form.perNight')}>
            <input
              type="number"
              required
              min={0}
              value={ratePerNight}
              onChange={(e) => setRatePerNight(Number(e.target.value))}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label={t('contracts.form.currency')}>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </fieldset>

      <div className="grid grid-cols-3 gap-4">
        <Field label={t('contracts.form.guaranteedRooms')}>
          <input
            type="number"
            min={0}
            value={guaranteedRooms}
            onChange={(e) => setGuaranteedRooms(Number(e.target.value))}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label={t('contracts.form.validFrom')}>
          <input
            type="date"
            required
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label={t('contracts.form.validUntil')}>
          <input
            type="date"
            required
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        </Field>
      </div>

      <Field label={t('contracts.form.terms')}>
        <textarea
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
          rows={2}
          className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
        />
      </Field>

      <div className="flex justify-end">
        <Button type="submit" disabled={pending || !hotel.trim() || !airport.trim()}>
          {pending ? t('contracts.form.saving') : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-foreground/80">{label}</span>
      {children}
    </label>
  );
}
